import React, { useEffect, useRef, useState } from 'react'
import { Text } from '../../../../modules/Dashboard'
import { motion, AnimatePresence } from 'framer-motion'
import { VerticalGradient } from './VerticalGradient'
import Spinner from './Spinner'

const LOADING_TITLE = [
    'finalizing',
    'collecting-insights',
    'analyzing-market-trends',
    'preparing-your-report',
]

const LOADING_ELE = [
    'w-[240px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[270px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[250px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[240px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[270px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[220px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[260px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[240px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[270px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[220px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
    'w-[260px] !h-[30px] border border-dashed border-gray-300 bg-gray-500 rounded-lg',
]

// FinalStep: dynamic clip + seamless duplicated-list scroll
export default function FinalStep({ done }) {
    const [currentTitle, setCurrentTitle] = useState(0)

    // refs for measuring and animating
    const containerRef = useRef(null)
    const listRef = useRef(null) // contains duplicated items
    const firstItemRef = useRef(null) // used to measure single item height

    const [clipHeight, setClipHeight] = useState(null)
    const [singleListHeight, setSingleListHeight] = useState(0)

    // --- Title slider ---
    useEffect(() => {
        const t = setInterval(() => {
            setCurrentTitle(prev => (prev + 1) % LOADING_TITLE.length)
        }, 3000)
        return () => clearInterval(t)
    }, [])

    // --- Measure sizes and compute heights for clipping and scrolling ---
    useEffect(() => {
        const measure = () => {
            if (!firstItemRef.current || !listRef.current || !containerRef.current) return

            const itemRect = firstItemRef.current.getBoundingClientRect()
            const itemHeight = Math.round(itemRect.height)

            // read computed rowGap (fallback to 0)
            const listStyle = window.getComputedStyle(listRef.current)
            const gap = parseFloat(listStyle.rowGap) || 0

            // how many items we want visible at once (>= 3 as requested)
            const visibleCount = Math.max(7, Math.min(LOADING_ELE.length, 7))

            // compute clip height so items keep their natural height
            const clip = Math.round(itemHeight * visibleCount + gap * (visibleCount - 1))
            setClipHeight(clip)

            // the listRef contains duplicated items: totalScrollHeight / 2 = single list height
            const totalScrollHeight = Math.round(listRef.current?.scrollHeight)
            const singleHeight = Math.round(totalScrollHeight / 2)
            setSingleListHeight(singleHeight)
        }

        // measure after paint so CSS is applied
        const rafId = requestAnimationFrame(measure)
        window.addEventListener('resize', measure)
        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener('resize', measure)
        }
    }, [])

    // speed in pixels per second (tweakable)
    const SPEED_PX_PER_SEC = 20
    const duration = singleListHeight ? Math.max(3, singleListHeight / SPEED_PX_PER_SEC) : 6

    return (
        <>
            <VerticalGradient className={"absolute top-0 left-0 !w-[1px] !h-[428px]"} />
            <VerticalGradient className={"absolute top-0 right-0 !w-[1px] !h-[428px]"} />

            <div className='absolute top-3 ltr:right-3 rtl:left-3'>
                <Spinner />
            </div>

            {/* Title slider (single-line, no fixed height messing with children) */}
            <div className='w-full overflow-hidden relative'>
                <div className='h-[40px]'>
                    <AnimatePresence mode='wait'>
                        <motion.div
                            key={currentTitle}
                            initial={{ y: 40, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -40, opacity: 0 }}
                            transition={{ duration: 0.45 }}
                            className='absolute left-0 w-full mx-2'
                        >
                            <Text
                                tid={LOADING_TITLE[currentTitle]}
                                textShadow="0 0 10px #fc6432, 0 0 5px #fc6432, 0 0 10px #fc6432"
                                className="text-2xl font-semibold !text-[#f1d6cd]"
                            />
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
            <div className='flex flex-col items-start justify-center p-7 mx-auto gap-8 h-[200px] overflow-visible border border-dashed rounded-xl mt-5'>


                {/* Scroller: container has a dynamic height (clipHeight) computed from the real item height so children don't get squashed */}
                <div
                    ref={containerRef}
                    className='w-full overflow-hidden'
                    style={clipHeight ? { height: `${clipHeight}px` } : undefined}
                >
                    {/* listRef contains the duplicated items for a seamless loop */}
                    <motion.div
                        ref={listRef}
                        className='flex flex-col gap-2 will-change-transform'
                        // animate translateY from 0 to -singleListHeight
                        animate={singleListHeight ? { y: -singleListHeight } : { y: 0 }}
                        transition={
                            singleListHeight
                                ? { y: { repeat: Infinity, repeatType: 'loop', ease: 'linear', duration } }
                                : {}
                        }
                    >
                        {[...LOADING_ELE, ...LOADING_ELE].map((cls, i) => (
                            <div key={i} ref={i === 0 ? firstItemRef : null} className={cls} />
                        ))}
                    </motion.div>
                </div>

            </div>
        </>
    )
}
